/**
 * Auto-layout des nœuds IVR via dagre (port 1:1 Next).
 * Les sorties sont à droite → flow gauche-à-droite par défaut.
 */
import dagre from 'dagre'
import type { Edge, Node } from '@xyflow/react'

const NODE_WIDTH = 220
const NODE_BASE_HEIGHT = 80

/** Estime la hauteur d'un nœud selon son nombre de handles de sortie. */
function estimateNodeHeight(nodeId: string, edges: Edge[]): number {
  const outputCount = edges.filter((e) => e.source === nodeId).length
  if (outputCount > 2) return NODE_BASE_HEIGHT + (outputCount - 1) * 28
  return NODE_BASE_HEIGHT
}

interface LayoutOptions {
  direction?: 'TB' | 'LR'
  nodeSpacingX?: number
  nodeSpacingY?: number
}

export function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {},
): Node[] {
  const {
    direction = 'LR',
    nodeSpacingX = 80,
    nodeSpacingY = 50,
  } = options

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: direction,
    nodesep: nodeSpacingY,
    ranksep: nodeSpacingX,
    marginx: 40,
    marginy: 40,
  })

  for (const node of nodes) {
    const height = estimateNodeHeight(node.id, edges)
    g.setNode(node.id, { width: NODE_WIDTH, height })
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  dagre.layout(g)

  return nodes.map((node) => {
    const dagreNode = g.node(node.id)
    if (!dagreNode) return node
    return {
      ...node,
      position: {
        x: dagreNode.x - NODE_WIDTH / 2,
        y: dagreNode.y - (dagreNode.height || NODE_BASE_HEIGHT) / 2,
      },
    }
  })
}
